import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAppContext } from '../context/AppContext';
import VerticalStepper_GroupAnalysis from './VerticalStepperRe_GroupAnalysis';
import ReactApexChart from "react-apexcharts";
import { FormControlLabel, Checkbox } from '@material-ui/core';
import createChart from '../Scripts/_createGroupAnalysisChart';
import createAthleteHistoryChart from '../Scripts/createAthleteHistoryChart';
import getInputGroupings from '../Scripts/_getInputGroupings';
import Swal from 'sweetalert2';

const GroupAnalysisTabContent = () => {
  const {
    norms,
    currentCampus,
    currentProgram,
    currentGender,
    currentDataType,
    athleteNamesList,
    currentAthleteEvaluations,
    currentAthleteSessionIndex,
    INPUT_SETTINGS,
    activeStep,
    setActiveStep,
    flask_post,
    loadAthleteEvaluations,
    athlete,
    setAthlete,
    setCurrentAthleteSessionIndex,
    currentAthleteSessionList,
    activeTest,
    setActiveTest,
    groupAnalysisObject,
    setGroupAnalysisObject
  } = useAppContext();
  
  const [showGroups, setShowGroups] = useState(true);
  const [showNorms, setShowNorms] = useState(true);
  const [showHighlightedAthlete, setShowHighlightedAthlete] = useState(true);
  const [showRebuildAthletes, setShowRebuildAthletes] = useState(false);
  const [showPercentiles, setShowPercentiles] = useState(false);
  
  const lastClickRef = useRef(null);
  const preservedSelection = useRef(null);

  const program = useMemo(() => {
    if (athlete) return athlete.program;
    if (currentProgram !== "All") return `${currentProgram} ATHlete`;
    return currentProgram;
  }, [athlete, currentProgram]);

  const testGrouping = useMemo(() => getInputGroupings('Gold', program, false, "group"), [program]);  

  useEffect(() => {
    // This effect synchronizes the UI state after an athlete is loaded, attempting to
    // preserve the user's previous test selection where possible.
    const selection = preservedSelection.current;
    preservedSelection.current = null; // Consume the preserved state immediately.

    if (testGrouping && selection) {
      // Stage 1: Try for an ideal match (same program, group, and test).
      if (program === selection.program) {
        const groupIndex = testGrouping.findIndex(g => g.groupName === selection.groupName);
        if (groupIndex !== -1) {
          const testIndex = testGrouping[groupIndex].inputTags.findIndex(t => t === selection.testName);
          setActiveStep(groupIndex);
          setActiveTest(testIndex !== -1 ? testIndex : 0);
          return; // Ideal match found.
        }
      }

      // Stage 2: Best-effort fallback (find same group name in the new program).
      const groupIndex = testGrouping.findIndex(g => g.groupName === selection.groupName);
      if (groupIndex !== -1) {
        const testIndex = testGrouping[groupIndex].inputTags.findIndex(t => t === selection.testName);
        setActiveStep(groupIndex);
        setActiveTest(testIndex !== -1 ? testIndex : 0);
        return; // Best-effort match found.
      }
    }
    
    // Fallback: If no match was found or no state was preserved, ensure the current step is valid.
    if (testGrouping && activeStep >= testGrouping.length) {
      setActiveStep(0);
      setActiveTest(0);
    }
  }, [testGrouping, program, activeStep, setActiveStep, setActiveTest]);

  const fetchGroupData = useCallback(async () => {
    Swal.fire({
      title: 'Preparing group analysis',
      text: 'Please wait. This may take a few seconds.',
      didOpen: () => { Swal.showLoading() },
      allowOutsideClick: false,
    });
    
    const response = await flask_post('LoadGroupData', ['All', 'All', 'All']);
    const groupData = response.data;
    setGroupAnalysisObject(groupData);
  
    Swal.close();
  }, [flask_post, setGroupAnalysisObject]);

  useEffect(() => {
    if (groupAnalysisObject && Object.keys(groupAnalysisObject).length === 0) {
      fetchGroupData();
    }
  }, [groupAnalysisObject, fetchGroupData]);
  
  useEffect(() => {
    if (!athlete) {
      setShowGroups(true);
    }
  }, [athlete]);

  const inputTags = useMemo(() => {
    // This check prevents a crash during the re-render that happens after an athlete is loaded
    // but before the useEffect has had a chance to correct the activeStep.
    if (!testGrouping || !testGrouping[activeStep]) {
      return [];
    }
    return testGrouping[activeStep].inputTags;
  }, [testGrouping, activeStep]);

  const testToPlot   = useMemo(() => inputTags ? inputTags[activeTest] : '', [inputTags, activeTest]);
  
  const norms_available = useMemo(() => testToPlot in norms, [testToPlot, norms]);

  const handleMarkerDoubleClick = useCallback((customTooltip) => {
    const selectedAthlete = customTooltip.substring(customTooltip.indexOf(' for\n ') + 6, customTooltip.indexOf(' at\n '));

    // Preserve the full context of the current selection.
    preservedSelection.current = {
      program: program,
      groupName: testGrouping[activeStep]?.groupName,
      testName: testGrouping[activeStep]?.inputTags[activeTest]
    };

    flask_post('LoadAthlete', [selectedAthlete]).then(response => {
      const newAthlete = response.data;
      setAthlete(newAthlete);
      setActiveStep(0);
      setActiveTest(0);
      loadAthleteEvaluations(newAthlete, currentDataType);

      const selectedEvalDate = customTooltip.substring(customTooltip.indexOf(' on\n ') + 5, customTooltip.indexOf(' [')).replace(/\//g, '-');
      const selectedEvalTime = customTooltip.substring(customTooltip.indexOf(' at\n ') + 5, customTooltip.indexOf(' on\n '));

      let selectedAthleteSessionIndex = currentAthleteSessionList.findIndex(item => item.includes(selectedEvalDate) && item.includes(selectedEvalTime));
      
      if (selectedAthleteSessionIndex === -1 || selectedAthleteSessionIndex.length > 1) {
        selectedAthleteSessionIndex = 0;
      }
      
      setCurrentAthleteSessionIndex(selectedAthleteSessionIndex);
    });
  }, [flask_post, setAthlete, loadAthleteEvaluations, currentAthleteSessionList, setCurrentAthleteSessionIndex, currentDataType, program, testGrouping, activeStep, activeTest]);

  const currentEval = useMemo(() => currentAthleteEvaluations.length ? currentAthleteEvaluations[currentAthleteSessionIndex] : [], [currentAthleteEvaluations, currentAthleteSessionIndex]);

  const inputSetting = useMemo(() => INPUT_SETTINGS.find(setting => setting.tag === testToPlot), [INPUT_SETTINGS, testToPlot]);
  const inputStringName = inputSetting ? inputSetting.inputStringName : '';

  const chartOptions = useMemo(() => {
    return showGroups 
      ? createChart(groupAnalysisObject, showGroups, norms, showNorms, currentEval, showHighlightedAthlete,
          currentCampus, currentProgram, currentGender, athleteNamesList,
          testToPlot, inputStringName, 
          handleMarkerDoubleClick, lastClickRef, showRebuildAthletes, showPercentiles && norms_available)
      : createAthleteHistoryChart(norms, showNorms, currentAthleteEvaluations, testToPlot, 
          inputStringName, handleMarkerDoubleClick, lastClickRef, showPercentiles && norms_available);
  }, [showGroups, groupAnalysisObject, norms, showNorms, currentEval, showHighlightedAthlete,
      currentCampus, currentProgram, currentGender, athleteNamesList,
      testToPlot, inputStringName, handleMarkerDoubleClick, showRebuildAthletes,
      showPercentiles, norms_available, currentAthleteEvaluations]);

  return (
    <>
      <div className='ActiveAthleteData'>    
        <div className='ActiveAthleteDataOverlay'>
          <div className="ActiveAthleteDataOverlayInputGroupings">
            <div style={{display: 'flex', flexDirection: 'row', width: '100%', paddingTop: '2%'}}>
              <div className='ActiveAthleteVerticalStepperDiv' style={{display: 'flex', minWidth: '30%', flexShrink: 0}}>
                <VerticalStepper_GroupAnalysis />
              </div>
              {chartOptions?.series.length > 0 ? 
              <div id="chart" style={{flexGrow: 1, maxWidth: '70%',  paddingLeft: '2%',  paddingRight: '2%'}}>
                <ReactApexChart 
                  options={chartOptions.options} 
                  series={chartOptions.series}  
                  type={chartOptions.type} 
                  height={600} 
                />
              </div>
              : <div style={{flexGrow: 1, maxWidth: '70%',  paddingLeft: '2%',  paddingRight: '2%'}}></div>}
            </div>
          </div>
        </div>
            <div style={{display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'center'}}> 
              <FormControlLabel 
                control={<Checkbox style={{ color: '#FDBA31'}} checked={showPercentiles} onChange={() => setShowPercentiles(!showPercentiles)} />}
                label="Show percentiles"
              />   
              {!showPercentiles && norms_available &&             
              <FormControlLabel 
                control={<Checkbox style={{ color: '#FDBA31'}} checked={showNorms} onChange={() => setShowNorms(!showNorms)} />}
                label="Show norms"
              />
              }
              {athlete &&                
              <FormControlLabel 
                control={<Checkbox style={{ color: '#FDBA31'}} checked={showGroups} onChange={() => setShowGroups(!showGroups)} />}
                label="Show other athletes"
              />}
              {athlete && showGroups &&
                <FormControlLabel 
                  control={<Checkbox style={{ color: '#FDBA31'}} checked={showHighlightedAthlete} onChange={() => setShowHighlightedAthlete(!showHighlightedAthlete)} />}
                  label="Show highlighted athlete"
                />
              }  
              {showGroups &&         
              <FormControlLabel 
                control={<Checkbox style={{ color: '#FDBA31'}} checked={showRebuildAthletes} onChange={() => setShowRebuildAthletes(!showRebuildAthletes)} />}
                label="Show rebuild"
              />
              }
            </div>
      </div>        
    </>
  );
};

export default GroupAnalysisTabContent;